import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../Api/apiClient'; // Import the apiClient

// Email validation function
const validateEmail = email => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const disallowedPatterns = ['info@', '@outlook', 'abc@', '0174453455@'];
  const repetitivePattern = /(\d)\1{2,}@/;
  const sequencePattern = /123@/;
  const disallowedDomains = ['info', 'outlook'];

  if (!re.test(String(email).toLowerCase())) return false;

  if (
    disallowedPatterns.some(pattern => email.toLowerCase().startsWith(pattern))
  )
    return false;

  if (
    repetitivePattern.test(email.toLowerCase()) ||
    sequencePattern.test(email.toLowerCase())
  )
    return false;

  const domain = email.split('@')[1].split('.')[0].toLowerCase();
  if (disallowedDomains.includes(domain)) return false;

  return true;
};

// Component for sending contact form
const SendContact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
  });
  const [errors, setErrors] = useState({});
  const isAuthenticated = localStorage.getItem('bearer') !== null;
  const userType = localStorage.getItem('user_type');

  // Fetch profile data if authenticated
  const fetchProfileData = async () => {
    if (isAuthenticated) {
      try {
        const response = await apiClient.get('/user/profile');
        const { full_name, email, phone } = response.data.data;

        setFormData({
          name: full_name || '',
          email: email || '',
          phone: phone || '',
          // subject: formData.subject || '',
        });
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  // Handle input changes
  const handleChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors({ ...errors, [e.target.name]: '' }); // Clear errors on input change
  };

  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault(); // Prevent default form submission

    let formErrors = {};
    if (!formData.email) {
      formErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      formErrors.email =
        'Not Accepted. Please avoid info@, @outlook, 000@, 111@, abc@, 123@, 0174453455@ emails.';
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors); // Set error messages if validation fails
      return;
    }

    // Define the API endpoint based on authentication status
    // const apiEndpoint = isAuthenticated
    //   ? '/user/tender-user/contact-us' // Authenticated endpoint
    //   : '/user/visitor/contact-us'; // Non-authenticated endpoint
    const apiEndpoint =
      userType === 'user' || userType === 'end_user'
        ? '/user/tender-user/contact-us'
        : userType === 'affiliator'
        ? '/affiliator/review/contact-us'
        : '/user/visitor/contact-us';

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // Make the API call
    try {
      const response = await apiClient.post(apiEndpoint, formData, config);
      const successMessage = response.data.message;

      Swal.fire({
        title: 'Form submitted successfully!',
        text: successMessage,
        customClass: {
          popup: 'w-72 h-auto p-3',
          title: 'text-sm',
          content: 'text-xs',
          confirmButton: 'bg-headerBtn text-white px-4 py-1 text-sm rounded-md',
        },
      });

      // Clear form fields
      setFormData({ name: '', email: '', phone: '', subject: '' });
    } catch (error) {
      const errorMessage = error.response?.data.message || 'An error occurred';

      Swal.fire({
        title: 'Failed to submit the form',
        text: errorMessage,
        customClass: {
          popup: 'w-72 h-auto p-3',
          title: 'text-sm',
          content: 'text-xs',
          confirmButton: 'bg-headerBtn text-white px-4 py-1 text-sm rounded-md',
        },
      });
      console.error('There was an error!', error);
    }
  };

  return (
    <div className="w-full mx-auto md:py-8 py-6 md:px-20 px-4 bg-AboutUsBg">
      <div className="flex justify-center">
        <div className="bg-white p-10 rounded-3xl shadow-xl">
          <form className="grid grid-cols-1 gap-3 w-64" onSubmit={handleSubmit}>
            {/* Name Input */}
            <input
              type="text"
              name="name"
              placeholder="Name"
              value={formData.name}
              onChange={handleChange}
              required
              className="border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-headerBtn"
            />

            {/* Email Input */}
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              className={`border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-headerBtn ${
                errors.email ? 'border-red-500' : ''
              }`}
              required
            />
            {errors.email && (
              <p className="text-red-500 text-xs text-center -mt-4">
                {errors.email}
              </p>
            )}

            {/* Phone Input */}
            <input
              type="text"
              name="phone"
              placeholder="Phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-headerBtn"
            />

            {/* Subject Input */}
            <textarea
              type="text"
              name="subject"
              placeholder="Message"
              value={formData.subject}
              onChange={handleChange}
              required
              className="border border-gray-300 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-headerBtn"
            />

            {/* Submit Button */}
            <button
              type="submit"
              className="bg-headerBtn text-white py-1 w-1/2 mx-auto rounded-md hover:bg-btnHover transition duration-300"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SendContact;

// import React, { useState, useEffect } from 'react';
// import Swal from 'sweetalert2';
// import { useNavigate } from 'react-router-dom';
// import apiClient from '../../Api/apiClient'; // Import the apiClient

// // Email validation function
// const validateEmail = email => {
//   const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//   const disallowedPatterns = ['info@', '@outlook', 'abc@', '0174453455@'];
//   const repetitivePattern = /(\d)\1{2,}@/;
//   const sequencePattern = /123@/;
//   const disallowedDomains = ['info', 'outlook'];

//   if (!re.test(String(email).toLowerCase())) {
//     return false;
//   }

//   if (
//     disallowedPatterns.some(pattern => email.toLowerCase().startsWith(pattern))
//   ) {
//     return false;
//   }

//   if (
//     repetitivePattern.test(email.toLowerCase()) ||
//     sequencePattern.test(email.toLowerCase())
//   ) {
//     return false;
//   }

//   const domain = email.split('@')[1].split('.')[0].toLowerCase();
//   if (disallowedDomains.includes(domain)) {
//     return false;
//   }

//   return true;
// };

// const SendContact = () => {
//   const navigate = useNavigate();
//   const [formData, setFormData] = useState({
//     name: '',
//     email: '',
//     phone: '',
//     subject: '',
//   });
//   const [errors, setErrors] = useState({});
//   const isAuthenticated = localStorage.getItem('bearer') !== null;

//   if (isAuthenticated) {
//     const fetchProfileData = async () => {
//       try {
//         const response = await apiClient.get('/user/profile');

//         console.log(response.data.data);

//         setFormData({
//           name: response.data.data.full_name || '',
//           email: response.data.data.email || '',
//           phone: response.data.data.phone || '',
//         });

//       } catch (error) {
//         console.error('Error fetching profile data:', error);
//       }
//     };
//     useEffect(() => {
//       fetchProfileData();
//     }, []);
//   }

//   // Handle input changes
//   const handleChange = e => {
//     setFormData({
//       ...formData,
//       [e.target.name]: e.target.value,
//     });
//     setErrors({ ...errors, [e.target.name]: '' }); // Clear errors on input change
//   };

//   // Handle form submission
//   const handleSubmit = async e => {
//     e.preventDefault(); // Prevent default form submission

//     let formErrors = {};
//     if (!formData.email) {
//       formErrors.email = 'Email is required';
//     } else if (!validateEmail(formData.email)) {
//       formErrors.email =
//         'Not Accepted. Please avoid info@, @outlook, 000@, 111@, abc@, 123@, 0174453455@ emails.';
//     }

//     if (Object.keys(formErrors).length > 0) {
//       setErrors(formErrors); // Set error messages if validation fails
//       return;
//     }

//     // Define the API endpoint based on authentication status
//     const apiEndpoint = isAuthenticated
//       ? '/user/tender-user/contact-us' // Authenticated endpoint
//       : '/user/visitor/contact-us'; // Non-authenticated endpoint

//     console.log(apiEndpoint);

//     const config = {
//       headers: {
//         'Content-Type': 'application/json',
//       },
//     };

//     // Use apiClient which already sets Authorization header if bearer token is available
//     try {
//       const response = await apiClient.post(apiEndpoint, formData, config);

//       const successMessage = response.data.message;
//       Swal.fire({
//         title: 'Form submitted successfully!',
//         text: successMessage,
//         customClass: {
//           popup: 'w-72 h-auto p-3',
//           title: 'text-sm',
//           content: 'text-xs',
//           confirmButton: 'bg-headerBtn text-white px-4 py-1 text-sm rounded-md',
//         },
//       });

//       // Clear form fields
//       setFormData({ name: '', email: '', subject: '', phone: '' });

//       // Optionally navigate after success
//       // navigate('/contactUs');
//     } catch (error) {
//       const errorMessage = error.response?.data.message || 'An error occurred';
//       Swal.fire({
//         title: 'Failed to submit the form',
//         text: errorMessage,
//         customClass: {
//           popup: 'w-72 h-auto p-3',
//           title: 'text-sm',
//           content: 'text-xs',
//           confirmButton: 'bg-headerBtn text-white px-4 py-1 text-sm rounded-md',
//         },
//       });
//       console.error('There was an error!', error);
//     }
//   };

//   return (
//     <div className="w-full mx-auto md:py-8 py-6 md:px-20 px-4 bg-AboutUsBg">
//       <div className="flex justify-center">
//         <div className="bg-white p-10 rounded-3xl shadow-xl">
//           <form className="grid grid-cols-1 gap-3 w-64" onSubmit={handleSubmit}>
//             <input
//               type="text"
//               name="name"
//               placeholder="Your Name"
//               value={formData.name}
//               onChange={handleChange}
//               required
//               className="border border-gray-300 p-3 rounded-3xl focus:outline-none focus:ring-2 focus:ring-headerBtn"
//             />
//             <input
//               type="email"
//               name="email"
//               placeholder="Your Email"
//               value={formData.email}
//               onChange={handleChange}
//               className={`border border-gray-300 p-3 rounded-3xl focus:outline-none focus:ring-2 focus:ring-headerBtn ${
//                 errors.email ? 'border-red-500' : ''
//               }`}
//               required
//             />
//             {errors.email && (
//               <p className="text-red-500 text-xs text-center -mt-4">
//                 {errors.email}
//               </p>
//             )}
//             <input
//               type="text"
//               name="phone"
//               placeholder="Phone"
//               value={formData.phone}
//               onChange={handleChange}
//               required
//               className="border border-gray-300 p-3 rounded-3xl focus:outline-none focus:ring-2 focus:ring-headerBtn"
//             />
//             <input
//               type="text"
//               name="subject"
//               placeholder="Subject"
//               value={formData.subject}
//               onChange={handleChange}
//               required
//               className="border border-gray-300 p-3 rounded-3xl focus:outline-none focus:ring-2 focus:ring-headerBtn"
//             />
//             <button
//               type="submit"
//               className="bg-headerBtn text-white py-2 w-1/2 mx-auto rounded-2xl hover:bg-btnHover transition duration-300"
//             >
//               Send
//             </button>
//           </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SendContact;

// import React, { useState } from 'react';
// import axios from 'axios';
// import Swal from 'sweetalert2';
// import { useNavigate } from 'react-router-dom';
// import apiClient from '../../Api/apiClient';

// // Email validation function
// const validateEmail = email => {
//   // General email format check
//   const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

//   // Disallowed specific patterns
//   const disallowedPatterns = ['info@', '@outlook', 'abc@', '0174453455@'];

//   // Regular expression for repetitive patterns (e.g., "000@", "111@", "123@")
//   const repetitivePattern = /(\d)\1{2,}@/; // Matches patterns like "000@", "111@", etc.
//   const sequencePattern = /123@/; // Matches sequences like "123@"

//   const disallowedDomains = ['info', 'outlook'];

//   // Check general format
//   if (!re.test(String(email).toLowerCase())) {
//     return false;
//   }

//   // Check for disallowed specific patterns
//   if (
//     disallowedPatterns.some(pattern => email.toLowerCase().startsWith(pattern))
//   ) {
//     return false;
//   }

//   // Check for repetitive and sequential patterns
//   if (
//     repetitivePattern.test(email.toLowerCase()) ||
//     sequencePattern.test(email.toLowerCase())
//   ) {
//     return false;
//   }

//   // Extract the email domain and check against disallowed domains
//   const domain = email.split('@')[1].split('.')[0].toLowerCase();
//   if (disallowedDomains.includes(domain)) {
//     return false;
//   }

//   return true;
// };

// // Main component
// const SendContact = () => {
//   const navigate = useNavigate();

//   // State for form data
//   const [formData, setFormData] = useState({
//     name: '',
//     email: '',
//     phone: '',
//     subject: '',
//   });

//   // State for validation errors
//   const [errors, setErrors] = useState({});

//   // Handle input change
//   const handleChange = e => {
//     setFormData({
//       ...formData,
//       [e.target.name]: e.target.value,
//     });
//     setErrors({ ...errors, [e.target.name]: '' }); // Clear the error on input change
//   };

//   // Handle form submission
//   const handleSubmit = async e => {
//     e.preventDefault(); // Prevent default form submission behavior

//     // Check for errors
//     let formErrors = {};
//     if (!formData.email) {
//       formErrors.email = 'Email is required';
//     } else if (!validateEmail(formData.email)) {
//       formErrors.email =
//         'Not Accepted. Please avoid info@, @outlook, 000@, 111@, abc@, 123@, 0174453455@ emails.';
//     }

//     if (Object.keys(formErrors).length > 0) {
//       setErrors(formErrors); // Set error messages if validation fails
//       return;
//     }

//     const config = {
//       headers: {
//         'Content-Type': 'application/json',
//       },
//     };

//     try {
//       const response = await apiClient.post(
//         '/user/tender-user/contact-us',
//         formData,
//         config
//       );
//       // Handle success
//       const successMessage = response.data.message
//         Swal.fire({
//           title: 'Form submitted successfully!',
//           // text: 'Our team will contact with you.',
//           text: successMessage,
//           customClass: {
//             popup: 'w-72 h-auto p-3',
//             title: 'text-sm',
//             content: 'text-xs',
//             confirmButton:
//               'bg-headerBtn text-white px-4 py-1 text-sm rounded-md',
//           },
//         });
//       console.log(response.data);

//       // Clear form fields
//       setFormData({ name: '', email: '', subject: '' , phone: ''});

//       // navigate('/contactUs');
//     } catch (error) {
//       // Handle error
//       const errorMessage = error.response?.data?.message
//       Swal.fire({
//         title: 'Failed to submit the form',
//         // text: 'Please try again.',
//         text: errorMessage,
//         customClass: {
//           popup: 'w-72 h-auto p-3',
//           title: 'text-sm',
//           content: 'text-xs',
//           confirmButton: 'bg-headerBtn text-white px-4 py-1 text-sm rounded-md',
//         },
//       });
//       console.error('There was an error!', error);
//     }
//   };

//   return (
//     <div className="w-full mx-auto md:py-8 py-6 md:px-20 px-4 bg-AboutUsBg">
//       <div className="flex justify-center">
//         <div className="bg-white p-10 rounded-3xl shadow-xl">
//           <form className="grid grid-cols-1 gap-3 w-64" onSubmit={handleSubmit}>
//             <input
//               type="text"
//               name="name"
//               placeholder="Your Name"
//               value={formData.name}
//               onChange={handleChange}
//               required
//               className="border border-gray-300 p-3 rounded-3xl focus:outline-none focus:ring-2 focus:ring-headerBtn"
//             />
//             <input
//               type="email"
//               name="email"
//               placeholder="Your Email"
//               value={formData.email}
//               onChange={handleChange}
//               className={`border border-gray-300 p-3 rounded-3xl focus:outline-none focus:ring-2 focus:ring-headerBtn ${
//                 errors.email ? 'border-red-500' : ''
//               }`}
//               required
//             />
//             {errors.email && (
//               <p className="text-red-500 text-xs text-center -mt-4">
//                 {errors.email}
//               </p>
//             )}
//             <input
//               type="text"
//               name="phone"
//               placeholder="phone"
//               value={formData.phone}
//               onChange={handleChange}
//               required
//               className="border border-gray-300 p-3 rounded-3xl focus:outline-none focus:ring-2 focus:ring-headerBtn"
//             />
//             <input
//               type="text"
//               name="subject"
//               placeholder="Subject"
//               value={formData.subject}
//               onChange={handleChange}
//               required
//               className="border border-gray-300 p-3 rounded-3xl focus:outline-none focus:ring-2 focus:ring-headerBtn"
//             />
//             <button
//               type="submit"
//               className="bg-headerBtn text-white py-2 w-1/2 mx-auto rounded-2xl hover:bg-btnHover transition duration-300"
//             >
//               Send
//             </button>
//           </form>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SendContact;
